import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import {
    Code,
    Smartphone,
    Globe,
    Rocket,
    Shield,
    Clock,
    MessageSquare,
    Zap,
    CheckCircle,
    Users,
    Building2
} from 'lucide-react'
import LandingHeader from '../../components/landing/LandingHeader'
import LandingFooter from '../../components/landing/LandingFooter'
import talkidaLogo from '../../assets/icons/logo.png'
import founderImage from '../../assets/images/founder.png'

const About = () => {
    const navigate = useNavigate()

    const handleGetStarted = () => {
        navigate('/signin')
    }

    const features = [
        {
            icon: Code,
            title: 'End-to-End Development',
            description: 'From design to deployment, we handle every aspect of your project'
        },
        {
            icon: Smartphone,
            title: 'Mobile App Development',
            description: 'Android and iOS apps with full Play Store and App Store release'
        },
        {
            icon: Globe,
            title: 'Web Solutions',
            description: 'Dynamic business dashboards and custom web applications'
        },
        {
            icon: Building2,
            title: 'Custom SaaS Platforms',
            description: 'Complex multi-vendor SaaS systems built to scale'
        },
        {
            icon: Rocket,
            title: 'Tech Products',
            description: 'We create our own innovative solutions like Talkida AI'
        },
        {
            icon: Shield,
            title: 'Enterprise Security',
            description: 'Bank-level security with professional communication and support'
        }
    ]

    const technologies = [
        'Flutter',
        'Python',
        'React JS',
        'Node JS'
    ]

    const benefits = [
        'One month free maintenance included',
        'Extendable maintenance plans',
        'Professional communication',
        'Strict timelines',
        '24x7 support via email and WhatsApp',
        'Experience with startups to large enterprises'
    ]

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5
            }
        }
    }

    return (
        <>
            <Helmet>
                <title>About Us | Talkida Technologies - End-to-End IT Solutions</title>
                <meta name="description" content="Talkida Technologies is an end-to-end IT solutions company specializing in mobile app development, web solutions, and custom SaaS platforms. We turn ideas into complete digital realities." />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
                <LandingHeader onGetStarted={handleGetStarted} />
                {/* Hero Section */}
                <section className="relative overflow-hidden pt-32 pb-16">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-purple-500/10 to-pink-500/10" />

                    <motion.div
                        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <motion.div
                            className="text-center mb-12"
                            variants={itemVariants}
                        >
                            <div className="flex items-center justify-center gap-3 mb-6">
                                <img src={talkidaLogo} alt="Talkida Technologies" className="h-16 w-auto" />
                                <h1 className="text-4xl md:text-5xl font-bold text-white">
                                    About Talkida Technologies
                                </h1>
                            </div>
                            <p className="text-xl text-white/80 max-w-3xl mx-auto">
                                Turning ideas into complete digital realities
                            </p>
                        </motion.div>
                    </motion.div>
                </section>

                {/* Founder Section */}
                <section className="relative py-16 bg-secondary-800/30">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            className="flex flex-col md:flex-row items-center justify-center gap-10"
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.div
                                variants={itemVariants}
                                className="flex-shrink-0"
                            >
                                <div className="w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden shadow-2xl border-4 border-primary-500/50 ring-4 ring-primary-500/20">
                                    <img
                                        src={founderImage}
                                        alt="Manjushree Barik, Founder of Talkida Technologies"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </motion.div>
                            <motion.div
                                variants={itemVariants}
                                className="text-center md:text-left max-w-2xl"
                            >
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                    Our Visionary Founder
                                </h2>
                                <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                                    <span className="font-semibold text-primary-400">Talkida Technologies</span> was founded by <span className="font-semibold text-primary-400">Manjushree Barik</span>, with a vision to make technology accessible, scalable, and affordable for every business. Her leadership drives our commitment to innovation and client success.
                                </p>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* Main Content */}
                <section className="relative py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="space-y-12"
                        >
                            {/* Introduction */}
                            <motion.div
                                variants={itemVariants}
                                className="bg-secondary-800/50 backdrop-blur-sm rounded-2xl p-8 border border-secondary-700"
                            >
                                <p className="text-lg text-white/90 leading-relaxed mb-6">
                                    At <span className="font-semibold text-primary-400">Talkida Technologies</span>, we turn ideas into complete digital realities. We are an end-to-end IT solutions company that takes your project from scratch to live, handling everything from design, development, deployment, hosting, and ongoing support.
                                </p>
                                <p className="text-lg text-white/90 leading-relaxed">
                                    We specialize in mobile app development, web solutions, and custom SaaS platforms. Whether it is an Android or iOS app with full Play Store or App Store release, a dynamic business dashboard, or a complex multi vendor SaaS system, we deliver with precision and quality.
                                </p>
                                <p className="text-sm text-white/60 mt-4 italic">
                                    Registered with MSME (Govt. of India) & ISTART (Govt. of Rajasthan)
                                </p>
                            </motion.div>

                            {/* Expertise Section */}
                            <motion.div
                                variants={itemVariants}
                                className="bg-secondary-800/50 backdrop-blur-sm rounded-2xl p-8 border border-secondary-700"
                            >
                                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                                    <Zap className="w-8 h-8 text-primary-400" />
                                    Our Expertise
                                </h2>
                                <p className="text-lg text-white/90 leading-relaxed mb-6">
                                    Our developers bring deep experience from startups to large scale enterprises, mastering technologies like{' '}
                                    {technologies.map((tech, index) => (
                                        <span key={tech}>
                                            <span className="font-semibold text-primary-400">{tech}</span>
                                            {index < technologies.length - 1 ? ', ' : ''}
                                        </span>
                                    ))}
                                    . This allows us to build powerful, scalable, and high performing solutions across industries.
                                </p>
                            </motion.div>

                            {/* Features Grid */}
                            <motion.div
                                variants={itemVariants}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                            >
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={feature.title}
                                        variants={itemVariants}
                                        className="bg-secondary-800/50 backdrop-blur-sm rounded-xl p-6 border border-secondary-700 hover:border-primary-500/50 transition-all duration-300"
                                    >
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="p-3 bg-primary-500/20 rounded-lg">
                                                <feature.icon className="w-6 h-6 text-primary-400" />
                                            </div>
                                            <h3 className="text-xl font-semibold text-white">
                                                {feature.title}
                                            </h3>
                                        </div>
                                        <p className="text-white/70">
                                            {feature.description}
                                        </p>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Our Products */}
                            <motion.div
                                variants={itemVariants}
                                className="bg-gradient-to-r from-primary-500/20 to-purple-500/20 backdrop-blur-sm rounded-2xl p-8 border border-primary-500/30"
                            >
                                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                                    <Rocket className="w-8 h-8 text-primary-400" />
                                    Our Products
                                </h2>
                                <p className="text-lg text-white/90 leading-relaxed">
                                    We also create our own tech products, beginning with <span className="font-semibold text-primary-400">Talkida AI</span>, a SaaS based smart chat solution that lets any business add an AI chatbot to their website in just five minutes. Our mission is to simplify technology for businesses, helping them grow without worrying about high costs or technical complexity.
                                </p>
                            </motion.div>

                            {/* Benefits */}
                            <motion.div
                                variants={itemVariants}
                                className="bg-secondary-800/50 backdrop-blur-sm rounded-2xl p-8 border border-secondary-700"
                            >
                                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                                    <CheckCircle className="w-8 h-8 text-primary-400" />
                                    Why Choose Us
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {benefits.map((benefit, index) => (
                                        <motion.div
                                            key={index}
                                            variants={itemVariants}
                                            className="flex items-start gap-3"
                                        >
                                            <CheckCircle className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-white/90">{benefit}</p>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Track Record */}
                            <motion.div
                                variants={itemVariants}
                                className="bg-secondary-800/50 backdrop-blur-sm rounded-2xl p-8 border border-secondary-700"
                            >
                                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                                    <Users className="w-8 h-8 text-primary-400" />
                                    Our Track Record
                                </h2>
                                <p className="text-lg text-white/90 leading-relaxed">
                                    Having delivered solutions for universities, startups, and established businesses, <span className="font-semibold text-primary-400">Talkida Technologies</span> continues to innovate and build software that empowers growth and efficiency.
                                </p>
                            </motion.div>

                            {/* Collaboration Section - Dracula Dark Theme */}
                            <motion.div
                                variants={itemVariants}
                                className="relative bg-gradient-to-br from-slate-900/90 to-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-700/50 shadow-2xl overflow-hidden"
                            >
                                {/* Glass morphism overlay */}
                                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent rounded-3xl"></div>
                                <div className="absolute inset-0 bg-gradient-to-tl from-purple-500/5 via-transparent to-cyan-500/5 rounded-3xl"></div>
                                
                                {/* Content */}
                                <div className="relative z-10">
                                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-100 mb-6 sm:mb-8 flex flex-col sm:flex-row items-center sm:items-center gap-3 text-center sm:text-left">
                                        <div className="p-2 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-xl backdrop-blur-sm border border-purple-400/30">
                                            <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-300" />
                                        </div>
                                        Open for Collaboration
                                    </h2>
                                    <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-8 sm:mb-10 text-center sm:text-left px-2 sm:px-0">
                                        We are always open to new partnerships and collaborations! Are you looking for a reliable technology partner? 
                                        Whether it's joint ventures, strategic partnerships, or collaborative projects, we'd love to explore opportunities together.
                                    </p>
                                    
                                    {/* CTA Glass Card */}
                                    <div className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-gray-600/30 text-center shadow-xl mx-2 sm:mx-0">
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent rounded-2xl"></div>
                                        <div className="relative z-10">
                                            <h3 className="text-lg sm:text-xl font-semibold text-gray-100 mb-4 sm:mb-6">Ready to Collaborate?</h3>
                                            <p className="text-sm sm:text-base text-gray-400 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
                                                Book a meeting with us today to discuss potential collaboration opportunities. 
                                                Let's build something amazing together!
                                            </p>
                                            
                                            <motion.a
                                                href="mailto:info@talkida.co.in?subject=Collaboration Inquiry&body=Hi Talkida Team,%0D%0A%0D%0AI'm interested in exploring collaboration opportunities with your company.%0D%0A%0D%0APlease let me know a suitable time for a meeting.%0D%0A%0D%0AThank you!"
                                                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white font-semibold rounded-xl transition-all duration-300 inline-flex items-center gap-2 shadow-xl hover:shadow-2xl border border-white/20 hover:border-white/30 text-sm sm:text-base"
                                                whileHover={{ scale: 1.05, y: -2 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                                                Book a Meeting
                                            </motion.a>
                                        </div>
                                    </div>
                                    
                                    {/* Features Grid */}
                                    <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 px-2 sm:px-0">
                                        <div className="text-center p-4 sm:p-6 bg-black/10 backdrop-blur-sm rounded-xl border border-gray-700/30 hover:border-purple-500/30 transition-all duration-300">
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl mx-auto mb-3 sm:mb-4 flex items-center justify-center backdrop-blur-sm border border-purple-400/20">
                                                <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-purple-300" />
                                            </div>
                                            <h4 className="text-gray-100 font-semibold mb-2 text-sm sm:text-base">Strategic Partnerships</h4>
                                            <p className="text-gray-400 text-xs sm:text-sm">Long-term business alliances</p>
                                        </div>
                                        <div className="text-center p-4 sm:p-6 bg-black/10 backdrop-blur-sm rounded-xl border border-gray-700/30 hover:border-cyan-500/30 transition-all duration-300">
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 rounded-xl mx-auto mb-3 sm:mb-4 flex items-center justify-center backdrop-blur-sm border border-cyan-400/20">
                                                <Code className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-300" />
                                            </div>
                                            <h4 className="text-gray-100 font-semibold mb-2 text-sm sm:text-base">Joint Development</h4>
                                            <p className="text-gray-400 text-xs sm:text-sm">Collaborative tech projects</p>
                                        </div>
                                        <div className="text-center p-4 sm:p-6 bg-black/10 backdrop-blur-sm rounded-xl border border-gray-700/30 hover:border-pink-500/30 transition-all duration-300">
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-pink-500/20 to-pink-600/20 rounded-xl mx-auto mb-3 sm:mb-4 flex items-center justify-center backdrop-blur-sm border border-pink-400/20">
                                                <Globe className="w-6 h-6 sm:w-7 sm:h-7 text-pink-300" />
                                            </div>
                                            <h4 className="text-gray-100 font-semibold mb-2 text-sm sm:text-base">Market Expansion</h4>
                                            <p className="text-gray-400 text-xs sm:text-sm">Explore new opportunities</p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Subtle animated background elements */}
                                <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-2xl animate-pulse"></div>
                                <div className="absolute bottom-10 left-10 w-24 h-24 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                            </motion.div>




                        </motion.div>
                    </div>
                </section>
                <LandingFooter />
            </div>
        </>
    )
}

export default About

